/**
 * WebEngage SDK helpers.
 *
 * The SDK is initialized once at app startup via initWebEngage() in index.js.
 * Use this module for all WebEngage interactions to avoid duplicate initialization.
 */

import {
  getLastSession,
  getSessionLoginAt,
  recordSessionLogout,
} from "../sessionDuration";

let identifiedUserId = null;
let identifiedLoginId = null;

const LOG_PREFIX = "[WebEngage]";
const SESSION_ID_KEY = "motion.webengageSessionId";
const USER_ID_KEY = "motion.webengageUserId";
const LOGIN_ID_KEY = "motion.webengageLoginId";
const MOBILE_CONTEXT_KEY = "motion.webengageMobileContext";
const PAYMENT_CONTEXT_KEY = "motion.webengagePaymentContext";
const DEFAULT_COUNTRY_CODE = "+91";

const getWebEngage = () => {
  if (typeof window === "undefined") return null;
  return window.webengage || null;
};

const safeSessionGet = (key) => {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSessionSet = (key, value) => {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // ignore storage failures
  }
};

const safeSessionRemove = (key) => {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // ignore storage failures
  }
};

const createSessionId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const getWebEngageSessionId = () => {
  if (typeof window === "undefined") return "";

  const existingSessionId = safeSessionGet(SESSION_ID_KEY);
  if (existingSessionId) return existingSessionId;

  const sessionId = createSessionId();
  safeSessionSet(SESSION_ID_KEY, sessionId);
  return sessionId;
};

const getPersistedUserId = () =>
  identifiedUserId || safeSessionGet(USER_ID_KEY) || undefined;

const normalizeCountryCode = (value) => {
  if (value === null || value === undefined || value === "") {
    return DEFAULT_COUNTRY_CODE;
  }

  const raw = String(value).trim();
  if (!raw) return DEFAULT_COUNTRY_CODE;

  const digits = raw.replace(/[^\d]/g, "");
  if (!digits) return DEFAULT_COUNTRY_CODE;

  return `+${digits}`;
};

const normalizeMobileDigits = (value) => {
  if (value === null || value === undefined || value === "") return "";

  const raw = String(value).trim();
  if (!raw) return "";

  if (raw.startsWith("+")) {
    return raw.replace(/[^\d]/g, "");
  }

  return raw.replace(/[^\d]/g, "");
};

const extractCountryCodeFromUser = (user = {}) =>
  user.country_code ||
  user.countryCode ||
  user.mobile_country_code ||
  user.phone_country_code ||
  user.isd_code ||
  user.isdCode ||
  user.dial_code ||
  user.dialCode;

const extractMobileFromUser = (user = {}, fallbackMobile = "") =>
  user.mobile ||
  user.mobile_number ||
  user.phone ||
  user.phone_number ||
  fallbackMobile ||
  "";

/**
 * Build mobile fields for WebEngage events and user attributes.
 * Includes country code so dashboard can show full numbers like +919877219835.
 */
export const getWebEngageMobileAttributes = (
  user = {},
  fallbackMobile = ""
) => {
  const countryCode = normalizeCountryCode(extractCountryCodeFromUser(user));
  const countryDigits = countryCode.replace("+", "");
  let mobileDigits = normalizeMobileDigits(
    extractMobileFromUser(user, fallbackMobile)
  );

  if (!mobileDigits) {
    return {};
  }

  // Strip leading country code if mobile already includes it (e.g. 919877219835).
  if (
    mobileDigits.startsWith(countryDigits) &&
    mobileDigits.length > countryDigits.length + 6
  ) {
    mobileDigits = mobileDigits.slice(countryDigits.length);
  }

  const mobileWithCountryCode = `+${countryDigits}${mobileDigits}`;

  return {
    mobile: mobileDigits,
    country_code: countryCode,
    mobile_with_country_code: mobileWithCountryCode,
  };
};

const saveWebEngageMobileContext = (mobileAttributes = {}) => {
  if (!mobileAttributes.mobile) return;
  safeSessionSet(MOBILE_CONTEXT_KEY, JSON.stringify(mobileAttributes));
};

const getWebEngageMobileContext = () => {
  const raw = safeSessionGet(MOBILE_CONTEXT_KEY);
  if (!raw) return {};

  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const clearWebEngageMobileContext = () => {
  safeSessionRemove(MOBILE_CONTEXT_KEY);
};

const isBlankAttributeValue = (value) =>
  value === undefined ||
  value === null ||
  value === "" ||
  value === "-" ||
  (typeof value === "string" && value.trim() === "");

const removeEmptyEventAttributes = (attributes) =>
  Object.entries(attributes || {}).reduce((result, [key, value]) => {
    if (!isBlankAttributeValue(value)) {
      result[key] = value;
    }
    return result;
  }, {});

const buildEventContext = () =>
  removeEmptyEventAttributes({
    user_id: getPersistedUserId(),
    ...getWebEngageMobileContext(),
    session_id: getWebEngageSessionId(),
    screen: typeof window !== "undefined" ? window.location.pathname : undefined,
    page_url: typeof window !== "undefined" ? window.location.href : undefined,
    event_timestamp: new Date().toISOString(),
  });

/**
 * Persist payment attrs so payment_gateway page can still log events
 * even after navigation / remount.
 */
export const saveWebEngagePaymentContext = (attributes = {}) => {
  const payload = removeEmptyEventAttributes({
    program_name: attributes.program_name || attributes.product_name,
    product_name: attributes.product_name || attributes.program_name,
    program_details: attributes.program_details,
    program_price: attributes.program_price ?? attributes.amount,
    final_price:
      attributes.final_price ??
      attributes.total_amount ??
      attributes.amount ??
      attributes.program_price,
    special_price:
      attributes.special_price ??
      attributes.discounted_price ??
      attributes.final_price ??
      attributes.amount,
    payment_mode: attributes.payment_mode,
    coupon_code: attributes.coupon_code,
    coupon_mode: attributes.coupon_mode || attributes.coupon_type,
    discount_amount:
      attributes.discount_amount != null ? attributes.discount_amount : 0,
    transaction_id: attributes.transaction_id || attributes.order_id,
    order_id: attributes.order_id,
    total_amount:
      attributes.total_amount ??
      attributes.final_price ??
      attributes.program_price ??
      attributes.amount,
    source: attributes.source || "web",
    product_id: attributes.product_id,
    amount: attributes.amount ?? attributes.final_price ?? attributes.program_price,
  });
  safeSessionSet(PAYMENT_CONTEXT_KEY, JSON.stringify(payload));
  // localStorage backup — Paytm redirects can remount before session is read
  try {
    window.localStorage.setItem(PAYMENT_CONTEXT_KEY, JSON.stringify(payload));
  } catch {
    // ignore
  }
  return payload;
};

export const getWebEngagePaymentContext = () => {
  const raw =
    safeSessionGet(PAYMENT_CONTEXT_KEY) ||
    (() => {
      try {
        return window.localStorage.getItem(PAYMENT_CONTEXT_KEY);
      } catch {
        return null;
      }
    })();
  if (!raw) return {};

  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

export const clearWebEngagePaymentContext = () => {
  safeSessionRemove(PAYMENT_CONTEXT_KEY);
  try {
    window.localStorage.removeItem(PAYMENT_CONTEXT_KEY);
  } catch {
    // ignore
  }
};

const setAttributeIfPresent = (webengage, key, value) => {
  if (value === null || value === undefined || value === "") {
    console.log(`${LOG_PREFIX} Skipped attribute "${key}" (no value)`);
    return;
  }

  webengage.user.setAttribute(key, value);
  console.log(`${LOG_PREFIX} setAttribute triggered`, { key, value });
};

const setWebEngageUserAttributes = (webengage, user = {}, fallbackMobile = "") => {
  const mobileAttributes = getWebEngageMobileAttributes(user, fallbackMobile);
  saveWebEngageMobileContext(mobileAttributes);

  console.log(`${LOG_PREFIX} Syncing user attributes...`, {
    email: user.email ?? null,
    name: user.name ?? null,
    mobile: mobileAttributes.mobile ?? null,
    country_code: mobileAttributes.country_code ?? null,
    mobile_with_country_code: mobileAttributes.mobile_with_country_code ?? null,
    dob: user.dob ?? null,
    gender: user.gender ?? null,
  });

  setAttributeIfPresent(webengage, "we_email", user.email);
  setAttributeIfPresent(webengage, "we_first_name", user.name);
  setAttributeIfPresent(
    webengage,
    "we_phone",
    mobileAttributes.mobile_with_country_code || user.mobile
  );
  setAttributeIfPresent(webengage, "we_birth_date", user.dob);
  setAttributeIfPresent(webengage, "we_gender", user.gender);
  setAttributeIfPresent(webengage, "country_code", mobileAttributes.country_code);
  setAttributeIfPresent(
    webengage,
    "mobile_with_country_code",
    mobileAttributes.mobile_with_country_code
  );
};

/**
 * Identifies the logged-in user in WebEngage and syncs available profile attributes.
 * WebEngage login uses mobile with country code, e.g. webengage.user.login("+919787219835").
 */
export const identifyWebEngageUser = (user = {}, options = {}) => {
  try {
    const appUserId = user?.user_id != null ? String(user.user_id) : "";
    const mobileAttributes = getWebEngageMobileAttributes(
      user,
      options.fallbackMobile
    );
    const loginId = mobileAttributes.mobile_with_country_code;

    if (!loginId) {
      console.warn(
        `${LOG_PREFIX} identifyWebEngageUser skipped (missing mobile)`,
        user
      );
      return;
    }

    const webengage = getWebEngage();
    if (!webengage?.user?.login) {
      console.warn(`${LOG_PREFIX} SDK not ready — user.login unavailable`);
      return;
    }

    console.log(`${LOG_PREFIX} identifyWebEngageUser called`, {
      userId: appUserId || undefined,
      loginId,
      ...mobileAttributes,
    });

    const previousLoginId =
      identifiedLoginId || safeSessionGet(LOGIN_ID_KEY) || "";

    if (previousLoginId !== loginId) {
      // Example: webengage.user.login("+919787219835");
      webengage.user.login(loginId);
      identifiedLoginId = loginId;
      safeSessionSet(LOGIN_ID_KEY, loginId);
      console.log(`${LOG_PREFIX} user.login triggered`, { loginId });
    } else {
      identifiedLoginId = loginId;
      safeSessionSet(LOGIN_ID_KEY, loginId);
      console.log(`${LOG_PREFIX} user.login skipped (already identified)`, {
        loginId,
      });
    }

    if (appUserId) {
      identifiedUserId = appUserId;
      safeSessionSet(USER_ID_KEY, appUserId);
    }

    setWebEngageUserAttributes(webengage, user, options.fallbackMobile);
    console.log(`${LOG_PREFIX} User identification complete`, {
      userId: appUserId || undefined,
      loginId,
      ...mobileAttributes,
    });
  } catch (error) {
    console.error(`${LOG_PREFIX} identifyWebEngageUser failed`, error);
  }
};

/**
 * Clears the WebEngage user session on logout.
 * Fires a logout event with time-on-site for this session.
 */
export const logoutWebEngageUser = () => {
  const previousLoginId =
    identifiedLoginId || safeSessionGet(LOGIN_ID_KEY) || undefined;
  const previousUserId =
    identifiedUserId || safeSessionGet(USER_ID_KEY) || undefined;
  const mobileAttributes = getWebEngageMobileContext();

  // Prefer last recorded session (auth reducer already ends the clock).
  // Fallback: compute from still-active login timestamp if present.
  let session = getLastSession();
  if (!session) {
    const loginAt = getSessionLoginAt();
    if (loginAt) {
      session = recordSessionLogout();
    }
  }

  const hasIdentity =
    previousLoginId || previousUserId || mobileAttributes.mobile;

  console.log(`${LOG_PREFIX} logoutWebEngageUser called`, {
    previousLoginId,
    previousUserId,
    sessionDuration: session?.durationFormatted,
  });

  // Always clear identity once; trackWebEngageEvent dedupes logout if called twice.
  if (hasIdentity) {
    trackWebEngageEvent("logout", {
      ...mobileAttributes,
      user_id: previousUserId,
      login_id: previousLoginId,
      login_at: session?.loginAtIso,
      logout_at: session?.logoutAtIso || new Date().toISOString(),
      session_duration_ms: session?.durationMs,
      session_duration: session?.durationFormatted,
      time_on_site: session?.durationFormatted,
    });
  }

  identifiedUserId = null;
  identifiedLoginId = null;
  safeSessionRemove(USER_ID_KEY);
  safeSessionRemove(LOGIN_ID_KEY);
  clearWebEngageMobileContext();
  clearWebEngagePaymentContext();

  const webengage = getWebEngage();
  if (!webengage?.user?.logout) {
    console.warn(`${LOG_PREFIX} SDK not ready — user.logout unavailable`);
    return;
  }

  webengage.user.logout();
  console.log(`${LOG_PREFIX} user.logout triggered`);
};

/**
 * Track successful user login (existing user OTP success).
 */
export const trackWebEngageLoginEvent = (user = {}, options = {}) => {
  identifyWebEngageUser(user, options);
  const mobileAttributes = getWebEngageMobileAttributes(
    user,
    options.fallbackMobile
  );

  trackWebEngageEvent("login", {
    ...mobileAttributes,
    user_id: user?.user_id != null ? String(user.user_id) : undefined,
    login_at: new Date().toISOString(),
    auth_method: options.authMethod || "otp",
  });
};

/**
 * Track successful user signup / registration.
 */
export const trackWebEngageSignupEvent = (user = {}, options = {}) => {
  identifyWebEngageUser(user, options);
  const mobileAttributes = getWebEngageMobileAttributes(
    user,
    options.fallbackMobile
  );

  trackWebEngageEvent("signup", {
    ...mobileAttributes,
    user_id: user?.user_id != null ? String(user.user_id) : undefined,
    signup_at: new Date().toISOString(),
    auth_method: options.authMethod || "otp",
  });
};

/**
 * Track a custom WebEngage event.
 *
 * Example:
 *   trackWebEngageEvent("Course Purchased", { course_id: "123", amount: 999 });
 *
 * Suggested places to add custom webengage.track() calls later:
 *   - After successful test or exercise submission
 *   - After course or package purchase completion
 *   - On important feature discovery or onboarding milestones
 *   - After profile or preference updates
 */

// Temporarily disabled from frontend tracking — keep helpers/attrs intact.
const DISABLED_WEBENGAGE_EVENTS = new Set([
  "Payment Initiated",
  "Payment Failed",
  "Payment Completed",
  "Course Purchased",
  "Course_purchased",
]);

const isWebEngageEventDisabled = (eventName) =>
  DISABLED_WEBENGAGE_EVENTS.has(eventName);

// Prevent the same logical event from firing twice in a short window
// (e.g. React remounts, double clicks, AuthSync + handler overlap).
const recentEventKeys = new Map();
const EVENT_DEDUPE_WINDOW_MS = 3000;

const buildEventDedupeKey = (eventName, attributes = {}) => {
  switch (eventName) {
    case "login":
    case "signup":
    case "logout":
      return `${eventName}:${
        attributes.mobile_with_country_code ||
        attributes.login_id ||
        attributes.user_id ||
        "auth"
      }`;
    case "buy_now":
      return `${eventName}:${
        attributes["Product Id"] ||
        attributes.product_id ||
        attributes["Product Name"] ||
        attributes.product_name ||
        attributes.user_id ||
        "product"
      }`;
    case "program_viewed":
      return `${eventName}:${attributes.Name || attributes.name || "product"}`;
    case "Add to Cart - Handbooks":
    case "Remove from Cart":
      return `${eventName}:${
        attributes["Product Id"] ||
        attributes.product_id ||
        attributes["Product Name"] ||
        "product"
      }`;
    case "View Coupons":
      return `${eventName}:bare`;
    case "Email Id Enter":
      return `${eventName}:${attributes.Email || attributes.email || "email"}`;
    case "Coupon Code Applied":
    case "Coupon Code Failed":
      return `${eventName}:${
        attributes["Coupon Code"] || attributes.coupon_code || "coupon"
      }`;
    case "Payment Initiated":
    case "Payment Failed":
    case "Payment Declined":
    case "Payment Completed":
    case "Course Purchased":
    case "Course_purchased":
      return `${eventName}:${
        attributes["Order ID"] ||
        attributes.order_id ||
        attributes["Transaction ID"] ||
        attributes.transaction_id ||
        "payment"
      }`;
    default:
      return null;
  }
};

const shouldSkipDuplicateEvent = (eventName, attributes = {}) => {
  const key = buildEventDedupeKey(eventName, attributes);
  if (!key) return false;

  const now = Date.now();
  const lastFiredAt = recentEventKeys.get(key);
  if (lastFiredAt && now - lastFiredAt < EVENT_DEDUPE_WINDOW_MS) {
    console.warn(`${LOG_PREFIX} skipped duplicate EVENT: ${eventName}`, {
      key,
      withinMs: EVENT_DEDUPE_WINDOW_MS,
    });
    return true;
  }

  recentEventKeys.set(key, now);
  return false;
};

export const trackWebEngageEvent = (eventName, eventData = {}) => {
  if (!eventName) {
    console.warn(`${LOG_PREFIX} track skipped — missing event name`, eventData);
    return;
  }

  if (isWebEngageEventDisabled(eventName)) {
    return;
  }

  // Drop blank/"-" values so dashboard never receives empty attributes.
  const attributes = sanitizeTrackAttributes({
    ...buildEventContext(),
    ...eventData,
  });

  if (shouldSkipDuplicateEvent(eventName, attributes)) {
    return;
  }

  const webengage = getWebEngage();
  if (!webengage?.track) {
    console.warn(`${LOG_PREFIX} EVENT (local only): ${eventName}`, attributes);
    return;
  }

  try {
    if (Object.keys(attributes).length === 0) {
      webengage.track(eventName);
    } else {
      webengage.track(eventName, attributes);
    }
    // Single console log per event fire.
    console.warn(`${LOG_PREFIX} EVENT: ${eventName}`, attributes);
  } catch (error) {
    console.error(`${LOG_PREFIX} EVENT FAILED: ${eventName}`, {
      attributes,
      error,
    });
  }
};

/**
 * Spec helpers: fire ONLY listed attributes that have real data.
 * Attribute keys match the WebEngage PRD names exactly.
 * Missing values are omitted entirely (never sent as "-").
 */
const attrValue = (value) => {
  if (isBlankAttributeValue(value)) return undefined;
  return value;
};

const attrNumber = (value) => {
  if (isBlankAttributeValue(value)) return undefined;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return undefined;
  return numeric;
};

/** Amounts as String — most WebEngage Title Case price attrs are typed String. */
const attrAmountString = (value) => {
  const numeric = attrNumber(value);
  if (numeric === undefined) return undefined;
  return String(numeric);
};

/** Keep only attribute keys that start with A–Z (Title Case PRD attrs). */
const keepCapitalizedAttributes = (eventData = {}) =>
  Object.entries(eventData || {}).reduce((result, [key, value]) => {
    if (/^[A-Z]/.test(key)) {
      result[key] = value;
    }
    return result;
  }, {});

const resolvePaymentProductName = (payload = {}) =>
  payload.product_name || payload.program_name || payload.name;

const resolveProgramPrice = (payload = {}) =>
  payload.program_price ??
  payload.mrp ??
  payload.price ??
  payload.amount ??
  payload.total_amount ??
  payload.final_price;

const resolveFinalPrice = (payload = {}) =>
  payload.final_price ??
  payload.total_amount ??
  payload.amount ??
  payload.special_price ??
  payload.discounted_price ??
  payload.program_price;

const resolveSpecialPrice = (payload = {}) =>
  payload.special_price ??
  payload.discounted_price ??
  payload.final_price ??
  payload.total_amount ??
  payload.amount ??
  payload.program_price;

const resolveCouponMode = (payload = {}) =>
  payload.coupon_mode || payload.coupon_type;

const sanitizeTrackAttributes = (eventData = {}) => {
  const cleaned = {};
  Object.entries(eventData || {}).forEach(([key, value]) => {
    if (isBlankAttributeValue(value)) return;
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed || trimmed === "-") return;
      cleaned[key] = trimmed;
      return;
    }
    cleaned[key] = value;
  });
  return cleaned;
};

/**
 * Track with ONLY the given attributes (no session/screen context).
 * Attributes without data are dropped entirely before webengage.track.
 */
export const trackWebEngageLeanEvent = (eventName, eventData = {}) => {
  if (!eventName) return;

  if (isWebEngageEventDisabled(eventName)) {
    return;
  }

  const attributes = sanitizeTrackAttributes(eventData);

  if (shouldSkipDuplicateEvent(eventName, attributes)) return;

  const webengage = getWebEngage();
  if (!webengage?.track) {
    console.warn(`${LOG_PREFIX} EVENT (local only): ${eventName}`, attributes);
    return;
  }

  try {
    if (Object.keys(attributes).length === 0) {
      webengage.track(eventName);
    } else {
      webengage.track(eventName, attributes);
    }
    console.warn(`${LOG_PREFIX} EVENT: ${eventName}`, attributes);
  } catch (error) {
    console.error(`${LOG_PREFIX} EVENT FAILED: ${eventName}`, {
      attributes,
      error,
    });
  }
};

const resolveProductId = (product = {}) =>
  product.package_id || product.p_id || product.product_id || product.id;

const resolveProductName = (product = {}) =>
  product.package_name ||
  product.product_name ||
  product.PackageInstallmentDetails?.package_name ||
  product.PackageInstallmentDetails?.product_name ||
  product.name ||
  product.title ||
  "";

const parseAmountValue = (value) => {
  if (value == null || value === "") return undefined;
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : undefined;
  }
  const cleaned = String(value).replace(/[^\d.-]/g, "");
  if (!cleaned) return undefined;
  const numeric = Number(cleaned);
  return Number.isFinite(numeric) ? numeric : undefined;
};

const resolveProductAmount = (product = {}, fallbackAmount) => {
  const candidates = [
    fallbackAmount,
    product.discounted_price,
    product.final_amount,
    product.total_price,
    product.course_price,
    product.price_details?.course_price,
    product.PackageInstallmentDetails?.discounted_price,
    product.price,
    product.amount != null && product.discount != null
      ? Number(product.amount) - Number(product.discount)
      : undefined,
    product.amount,
    product.product_price,
  ];

  for (const candidate of candidates) {
    const numeric = parseAmountValue(candidate);
    if (numeric != null && numeric > 0) return numeric;
  }

  return undefined;
};

const resolveProductDetails = (product = {}) =>
  product.description ||
  product.short_description ||
  product.PackageInstallmentDetails?.description ||
  product.duration ||
  product.validity ||
  "";

const resolveOffers = (product = {}) => {
  if (product.offer || product.offers) return product.offer || product.offers;
  if (product.discount_text) return product.discount_text;

  const discountCandidates = [
    product.discount,
    product.price_details?.discount,
    product.PackageInstallmentDetails?.discount,
  ];

  for (const candidate of discountCandidates) {
    if (candidate == null || candidate === "") continue;
    const numeric = Number(candidate);
    if (Number.isFinite(numeric) && numeric > 0) return String(numeric);
    const text = String(candidate).trim();
    if (text && text !== "0" && text !== "0.00") return text;
  }

  const amount = Number(
    product.amount ?? product.course_price ?? product.price
  );
  const discounted = Number(
    product.discounted_price ?? product.final_amount ?? product.total_price
  );
  if (
    Number.isFinite(amount) &&
    Number.isFinite(discounted) &&
    amount > discounted &&
    discounted >= 0
  ) {
    return String(amount - discounted);
  }

  return "";
};

const resolveDeeplink = (product = {}) => {
  const id = resolveProductId(product);
  if (typeof window !== "undefined" && window.location?.href) {
    return window.location.href;
  }
  if (id) return `/package/${id}`;
  return "";
};

const resolveScreenName = () =>
  typeof window !== "undefined" ? window.location.pathname : "";

/** Keep buildLeanCommerceAttributes for any legacy callers */
export const buildLeanCommerceAttributes = (attrs = {}) =>
  sanitizeTrackAttributes(
    keepCapitalizedAttributes({
      "Program Name": attrs.program_name || attrs.product_name,
      "Product Name": attrs.product_name || attrs.program_name,
      "Program Details": attrs.program_details,
      "Program Price": attrAmountString(attrs.program_price ?? attrs.amount),
      "Final Price": attrAmountString(
        attrs.final_price ?? attrs.total_amount ?? attrs.amount
      ),
      "Special Price": attrAmountString(
        attrs.special_price ?? attrs.discounted_price
      ),
      "Payment Mode": attrs.payment_mode,
      "Coupon Code": attrs.coupon_code,
      "Coupon Mode": attrs.coupon_mode || attrs.coupon_type,
      // Discount Amount is Number in WebEngage schema
      "Discount Amount": attrNumber(attrs.discount_amount),
      "Transaction ID": attrs.transaction_id,
      "Order ID": attrs.order_id,
      "Total Amount": attrAmountString(attrs.total_amount ?? attrs.amount),
      Source: attrs.source,
      Reason: attrs.reason,
    })
  );

/**
 * program_viewed — Name, Offers, Prices, Type
 * Only fires when name is non-empty and price is a finite number > 0
 */
export const trackWebEngageProgramViewed = (product = {}, extraAttributes = {}) => {
  const name = String(resolveProductName(product) || "").trim();
  const price = Number(resolveProductAmount(product));
  if (!name || !Number.isFinite(price) || price <= 0) {
    console.warn(`${LOG_PREFIX} program_viewed skipped — name/price gate`, {
      name,
      price,
    });
    return;
  }

  const isHandbook = Boolean(product.p_id || product.product_id);
  const offers =
    resolveOffers(product) ||
    extraAttributes.offers ||
    extraAttributes.Offers ||
    "";

  trackWebEngageLeanEvent("program_viewed", {
    Name: name,
    // Always include Offers (PRD attr) — use "0" when no active offer
    Offers: String(offers || "0"),
    Prices: price,
    Type: attrValue(
      extraAttributes.type || (isHandbook ? "handbook" : "package")
    ),
  });
};

/**
 * buy_now — Product Name, Product Details, Product Price, Deeplink_Url, Screen Name, user_id
 */
export const trackWebEngageBuyNow = (product = {}, extraAttributes = {}) => {
  const productPrice =
    resolveProductAmount(
      product,
      extraAttributes.product_price ?? extraAttributes["Product Price"]
    ) ?? 0;

  trackWebEngageLeanEvent("buy_now", {
    "Product Name": attrValue(resolveProductName(product)),
    "Product Details": attrValue(
      extraAttributes.product_details || resolveProductDetails(product)
    ),
    // Always include Product Price as Number (WebEngage schema)
    "Product Price": Number(productPrice),
    Deeplink_Url: attrValue(
      extraAttributes.deeplink_url || resolveDeeplink(product)
    ),
    "Screen Name": attrValue(
      extraAttributes.screen_name || resolveScreenName()
    ),
    user_id: attrValue(extraAttributes.user_id || getPersistedUserId()),
  });
};

/** View Coupons — no payload */
export const trackWebEngageViewCoupons = () => {
  trackWebEngageLeanEvent("View Coupons");
};

/** Coupon Code Applied */
export const trackWebEngageCouponApplied = ({
  coupon_code,
  order_coupon_code,
  discount_amount,
  cart_value_before_discount,
  cart_value_after_discount,
  source,
  coupon_type,
} = {}) => {
  trackWebEngageLeanEvent("Coupon Code Applied", {
    "Cart Value Before Discount": attrNumber(cart_value_before_discount),
    "Cart Value After Discount": attrNumber(cart_value_after_discount),
    "Order Coupon Code": attrValue(order_coupon_code || coupon_code),
    "Discount Amount": attrNumber(discount_amount),
    Source: attrValue(source),
    "Coupon Type": attrValue(coupon_type),
    "Coupon Code": attrValue(coupon_code),
  });
};

/** Coupon Code Failed */
export const trackWebEngageCouponFailed = ({ coupon_code, source } = {}) => {
  trackWebEngageLeanEvent("Coupon Code Failed", {
    "Coupon Code": attrValue(coupon_code),
    Source: attrValue(source),
  });
};

/** Email Id Enter */
export const trackWebEngageEmailEnter = (email) => {
  trackWebEngageLeanEvent("Email Id Enter", {
    Email: attrValue(email),
  });
};

/**
 * Payment Initiated — Capital-letter attrs only (no Payment Mode):
 * Final Price, Order ID, Product Name, Program Details,
 * Program Name, Program Price, Special Price
 *
 * Schema types observed in WebEngage:
 * - Final Price → String
 * - Program Price / Special Price → Number
 */
export const trackWebEngagePaymentInitiated = (payload = {}) => {
  const finalPrice = resolveFinalPrice(payload);
  const programPrice = resolveProgramPrice(payload) ?? finalPrice;
  const specialPrice = resolveSpecialPrice(payload) ?? finalPrice;

  trackWebEngageLeanEvent(
    "Payment Initiated",
    keepCapitalizedAttributes({
      "Final Price": attrAmountString(finalPrice),
      "Order ID": attrValue(payload.order_id),
      "Product Name": attrValue(resolvePaymentProductName(payload)),
      "Program Details": attrValue(payload.program_details),
      "Program Name": attrValue(
        payload.program_name || resolvePaymentProductName(payload)
      ),
      "Program Price": attrNumber(programPrice),
      "Special Price": attrNumber(specialPrice),
    })
  );
};

/**
 * Shared attrs for Payment Completed + Course Purchased:
 * Coupon Code, Coupon Mode, Discount Amount, Final Price, Order ID,
 * Payment Mode, Product Name, Program Details, Program Price,
 * Special Price, Transaction ID
 */
const buildPaymentSuccessAttributes = (payload = {}) => {
  const finalPrice = resolveFinalPrice(payload);
  const programPrice = resolveProgramPrice(payload) ?? finalPrice;
  const specialPrice = resolveSpecialPrice(payload) ?? finalPrice;

  return {
    "Coupon Code": attrValue(payload.coupon_code),
    "Coupon Mode": attrValue(resolveCouponMode(payload)),
    "Discount Amount": attrNumber(payload.discount_amount),
    "Final Price": attrAmountString(finalPrice),
    "Order ID": attrValue(payload.order_id),
    "Payment Mode": attrValue(payload.payment_mode),
    "Product Name": attrValue(resolvePaymentProductName(payload)),
    "Program Details": attrValue(payload.program_details),
    "Program Price": attrNumber(programPrice),
    "Special Price": attrNumber(specialPrice),
    "Transaction ID": attrValue(payload.transaction_id || payload.order_id),
  };
};

/**
 * Payment Completed — exact whitelist (see buildPaymentSuccessAttributes)
 */
export const trackWebEngagePaymentCompleted = (payload = {}) => {
  trackWebEngageLeanEvent(
    "Payment Completed",
    buildPaymentSuccessAttributes(payload)
  );
};

/**
 * Payment Failed — Capital-letter attrs only:
 * Coupon Code, Coupon Mode, Discount Amount, Final Price, Order ID,
 * Payment Mode, Reason, Source, Special Price, Total Amount, Transaction ID
 *
 * Price fields are filled from Payment Initiated context when missing,
 * and typed the same way as Payment Initiated (Final Price String,
 * Special Price Number).
 */
export const trackWebEngagePaymentFailed = (payload = {}) => {
  const savedContext = getWebEngagePaymentContext();
  const merged = {
    ...savedContext,
    ...payload,
    program_price:
      payload.program_price ??
      savedContext.program_price ??
      savedContext.amount,
    final_price:
      payload.final_price ??
      savedContext.final_price ??
      savedContext.total_amount ??
      savedContext.amount ??
      payload.amount ??
      savedContext.program_price,
    special_price:
      payload.special_price ??
      savedContext.special_price ??
      savedContext.discounted_price ??
      payload.final_price ??
      savedContext.final_price ??
      savedContext.amount,
    total_amount:
      payload.total_amount ??
      savedContext.total_amount ??
      payload.final_price ??
      savedContext.final_price ??
      payload.amount ??
      savedContext.amount,
    discount_amount:
      payload.discount_amount ?? savedContext.discount_amount ?? 0,
    amount:
      payload.amount ??
      savedContext.amount ??
      payload.final_price ??
      savedContext.final_price,
    source: payload.source || savedContext.source || "web",
    coupon_code: payload.coupon_code ?? savedContext.coupon_code,
    coupon_mode: payload.coupon_mode ?? savedContext.coupon_mode,
  };

  const finalPrice = resolveFinalPrice(merged);
  const specialPrice = resolveSpecialPrice(merged) ?? finalPrice;
  const totalAmount = resolveFinalPrice({
    ...merged,
    final_price: merged.total_amount ?? finalPrice,
  });

  trackWebEngageLeanEvent(
    "Payment Failed",
    keepCapitalizedAttributes({
      "Coupon Code": attrValue(merged.coupon_code),
      "Coupon Mode": attrValue(resolveCouponMode(merged)),
      "Discount Amount": attrNumber(merged.discount_amount),
      // Match Payment Initiated typing for price fields
      "Final Price": attrAmountString(finalPrice),
      "Order ID": attrValue(merged.order_id || payload.order_id),
      "Payment Mode": attrValue(merged.payment_mode || payload.payment_mode),
      Reason: attrValue(payload.reason || merged.reason),
      Source: attrValue(merged.source || "web"),
      "Special Price": attrNumber(specialPrice),
      "Total Amount": attrAmountString(totalAmount),
      "Transaction ID": attrValue(
        merged.transaction_id || merged.order_id || payload.order_id
      ),
    })
  );
};

/**
 * Course Purchased — same attrs as Payment Completed.
 * Fire immediately after success, before Payment Completed.
 */
export const trackWebEngageCoursePurchased = (payload = {}) => {
  trackWebEngageLeanEvent(
    "Course Purchased",
    buildPaymentSuccessAttributes(payload)
  );
};

/** Shared payment event router */
export const trackWebEngagePaymentEvent = (
  eventName,
  payload = {},
  extraAttributes = {}
) => {
  const merged = { ...payload, ...extraAttributes };
  if (eventName === "Payment Initiated") {
    trackWebEngagePaymentInitiated(merged);
    return;
  }
  if (eventName === "Payment Completed") {
    trackWebEngagePaymentCompleted(merged);
    return;
  }
  if (eventName === "Payment Failed") {
    trackWebEngagePaymentFailed(merged);
    return;
  }
  if (eventName === "Course_purchased" || eventName === "Course Purchased") {
    trackWebEngageCoursePurchased(merged);
    return;
  }
  trackWebEngageLeanEvent(eventName, buildLeanCommerceAttributes(merged));
};

export const trackWebEngageAddToCartHandbooks = (product = {}) => {
  trackWebEngageLeanEvent("Add to Cart - Handbooks", {
    "Product Name": attrValue(resolveProductName(product)),
    "Product Price": attrNumber(resolveProductAmount(product)),
    "Product Id": attrValue(resolveProductId(product)),
  });
};

export const trackWebEngageRemoveFromCart = (product = {}) => {
  trackWebEngageLeanEvent("Remove from Cart", {
    "Product Name": attrValue(resolveProductName(product)),
    "Product Price": attrNumber(resolveProductAmount(product)),
    "Product Id": attrValue(resolveProductId(product)),
  });
};